import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye as EyeIcon, History } from 'lucide-react';
import Layout from '../components/Layout';
import EstimatePreview from '../components/EstimatePreview';
import { estimateAPI, customerAPI, companyProfileAPI } from '../api/client';
import { useToast } from '../components/ToastProvider';
import { getLogo } from '../utils/logoUpload';

const ESTIMATE_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
const PROJECT_TYPES = ['Service', 'Product', 'Consulting', 'Maintenance', 'Design', 'Development', 'Other'];
const PAYMENT_TERMS = ['Net 30', 'Net 60', '50% Advance', 'Full Payment in Advance', 'Upon Completion', 'Custom'];

const Estimates = () => {
  const { showToast } = useToast();
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Preview modal state
  const [previewEstimate, setPreviewEstimate] = useState(null);
  const [previewLineItems, setPreviewLineItems] = useState([]);
  const [previewCustomer, setPreviewCustomer] = useState(null);
  const [previewCompany, setPreviewCompany] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    customer_id: '',
    project_name: '',
    project_type: 'Service',
    scope_of_work: '',
    estimate_date: new Date().toISOString().split('T')[0],
    validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    timeline_start: '',
    timeline_end: '',
    assumptions: '',
    payment_terms: 'Net 30',
    payment_method: 'Bank Transfer',
    special_notes: '',
    tax_percentage: 18,
    discount_type: 'percentage',
    discount_value: 0,
    line_items: [{ description: '', quantity: 1, unit_rate: 0, tax_applicable: true }],
  });

  // Load data
  useEffect(() => {
    loadEstimates();
    loadCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, approvalStatusFilter]);

  const loadEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusFilter) params.status = statusFilter;
      if (approvalStatusFilter) params.approvalStatus = approvalStatusFilter;
      const response = await estimateAPI.getAll(params);
      setEstimates(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      console.error('Error loading estimates:', err);
      showToast({ title: 'Error', message: 'Failed to load estimates', tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, approvalStatusFilter, showToast]);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll({ limit: 1000 });
      setCustomers(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  // Format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue || dateValue === '0000-00-00' || dateValue === '1899-11-30') {
      return '';
    }
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '';
      }
      // Return in YYYY-MM-DD format
      return date.toISOString().split('T')[0];
    } catch (err) {
      return '';
    }
  };

  // Calculate totals
  const calculateTotals = (items) => {
    let subtotal = 0;
    items.forEach(item => {
      subtotal += (parseFloat(item.quantity || 0) * parseFloat(item.unit_rate || 0));
    });
    const tax = (subtotal * parseFloat(formData.tax_percentage || 0)) / 100;
    const discount = formData.discount_type === 'percentage'
      ? (subtotal * parseFloat(formData.discount_value || 0)) / 100
      : parseFloat(formData.discount_value || 0);
    return { subtotal, tax, discount, total: subtotal + tax - discount };
  };

  const totals = calculateTotals(formData.line_items);

  // Form handlers
  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_rate: 0, tax_applicable: true }]
    });
  };

  const handleRemoveLineItem = (index) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index)
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    setFormData({ ...formData, line_items: newItems });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.line_items.length === 0) {
        showToast({ title: 'Error', message: 'Add at least one line item', tone: 'error' });
        return;
      }

      if (isEditing) {
        await estimateAPI.update(editingId, formData);
        showToast({ title: 'Success', message: 'Estimate updated successfully', tone: 'success' });
      } else {
        await estimateAPI.create(formData);
        showToast({ title: 'Success', message: 'Estimate created successfully', tone: 'success' });
      }

      resetForm();
      setShowForm(false);
      setPage(1);
      loadEstimates();
    } catch (err) {
      console.error('Error saving estimate:', err);
      showToast({ title: 'Error', message: err.response?.data?.error || 'Failed to save estimate', tone: 'error' });
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await estimateAPI.get(id);
      setFormData({
        customer_id: response.data.customer_id || '',
        project_name: response.data.project_name || '',
        project_type: response.data.project_type || 'Service',
        scope_of_work: response.data.scope_of_work || '',
        estimate_date: formatDateForInput(response.data.estimate_date) || new Date().toISOString().split('T')[0],
        validity_date: formatDateForInput(response.data.validity_date) || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        timeline_start: formatDateForInput(response.data.timeline_start),
        timeline_end: formatDateForInput(response.data.timeline_end),
        assumptions: response.data.assumptions || '',
        payment_terms: response.data.payment_terms || 'Net 30',
        payment_method: response.data.payment_method || 'Bank Transfer',
        special_notes: response.data.special_notes || '',
        tax_percentage: response.data.tax_percentage || 18,
        discount_type: response.data.discount_type || 'percentage',
        discount_value: response.data.discount_value || 0,
        line_items: response.data.line_items || []
      });
      setEditingId(id);
      setIsEditing(true);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching estimate:', err);
      showToast({ title: 'Error', message: 'Failed to load estimate', tone: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await estimateAPI.delete(id);
      showToast({ title: 'Success', message: 'Estimate deleted successfully', tone: 'success' });
      loadEstimates();
    } catch (err) {
      console.error('Error deleting estimate:', err);
      showToast({ title: 'Error', message: 'Failed to delete estimate', tone: 'error' });
    }
  };

  const handlePreview = async (id) => {
    try {
      const response = await estimateAPI.get(id);
      setPreviewEstimate(response.data);
      setPreviewLineItems(response.data.line_items || []);

      // Fetch customer data
      if (response.data.customer_id) {
        try {
          const customerResponse = await customerAPI.get(response.data.customer_id);
          setPreviewCustomer(customerResponse.data);
        } catch (err) {
          console.error('Error loading customer:', err);
          setPreviewCustomer({ name: `Customer ${response.data.customer_id}` });
        }
      }

      // Fetch company data
      try {
        const companyResponse = await companyProfileAPI.get();
        const logo = getLogo();
        setPreviewCompany({
          company: companyResponse.data.companyName || '',
          email: companyResponse.data.email || '',
          phone: companyResponse.data.phone || '',
          address: companyResponse.data.address || '',
          city: companyResponse.data.city || '',
          state: companyResponse.data.state || '',
          zipCode: companyResponse.data.zipCode || '',
          country: companyResponse.data.country || '',
          logo: logo || undefined,
        });
      } catch (err) {
        console.error('Error loading company profile:', err);
        setPreviewCompany({ logo: getLogo() || undefined });
      }

      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error fetching estimate:', err);
      showToast({ title: 'Error', message: 'Failed to load estimate', tone: 'error' });
    }
  };

  const handleShowVersions = async (id) => {
    try {
      const response = await estimateAPI.getVersions(id);
      setVersionHistory(response.data);
      setShowVersionHistory(true);
    } catch (err) {
      console.error('Error fetching versions:', err);
      showToast({ title: 'Error', message: 'Failed to load version history', tone: 'error' });
    }
  };

  const handleApprove = async () => {
    if (!previewEstimate) return;
    try {
      await estimateAPI.approve(previewEstimate.id);
      showToast({ title: 'Success', message: 'Estimate approved successfully', tone: 'success' });
      setShowPreviewModal(false);
      loadEstimates();
    } catch (err) {
      console.error('Error approving estimate:', err);
      showToast({ title: 'Error', message: 'Failed to approve estimate', tone: 'error' });
    }
  };

  const handleReject = async () => {
    if (!previewEstimate) return;
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await estimateAPI.reject(previewEstimate.id, { rejection_reason: reason });
      showToast({ title: 'Success', message: 'Estimate rejected', tone: 'success' });
      setShowPreviewModal(false);
      loadEstimates();
    } catch (err) {
      console.error('Error rejecting estimate:', err);
      showToast({ title: 'Error', message: 'Failed to reject estimate', tone: 'error' });
    }
  };

  const handleDownloadPdf = async () => {
    if (!previewEstimate) return;
    try {
      const logo = getLogo();
      const response = await estimateAPI.downloadPdf(previewEstimate.id, { logo });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estimate-${previewEstimate.estimateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      showToast({ title: 'Success', message: 'PDF downloaded successfully', tone: 'success' });
    } catch (err) {
      console.error('Error downloading PDF:', err);
      showToast({ title: 'Error', message: 'Failed to download PDF', tone: 'error' });
    }
  };

  const handleSendEmailWithApproval = async (id) => {
    try {
      const recipient = window.prompt('Enter recipient email address:');
      if (recipient) {
        await estimateAPI.sendEmail(id, { recipient_email: recipient });
        showToast({ title: 'Success', message: 'Estimate sent successfully', tone: 'success' });
        loadEstimates();
      }
    } catch (err) {
      console.error('Error sending estimate:', err);
      showToast({ title: 'Error', message: err.response?.data?.error || 'Failed to send estimate', tone: 'error' });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSendEmail = async (id) => {
    try {
      const recipient = window.prompt('Enter recipient email address:');
      if (recipient) {
        await estimateAPI.sendEmail(id, { recipient_email: recipient });
        showToast({ title: 'Success', message: 'Estimate sent successfully', tone: 'success' });
        loadEstimates();
      }
    } catch (err) {
      console.error('Error sending estimate:', err);
      showToast({ title: 'Error', message: 'Failed to send estimate', tone: 'error' });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAccept = async (id) => {
    try {
      await estimateAPI.accept(id);
      showToast({ title: 'Success', message: 'Estimate marked as accepted', tone: 'success' });
      loadEstimates();
    } catch (err) {
      console.error('Error accepting estimate:', err);
      showToast({ title: 'Error', message: 'Failed to accept estimate', tone: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      project_name: '',
      project_type: 'Service',
      scope_of_work: '',
      estimate_date: new Date().toISOString().split('T')[0],
      validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      timeline_start: '',
      timeline_end: '',
      assumptions: '',
      payment_terms: 'Net 30',
      payment_method: 'Bank Transfer',
      special_notes: '',
      tax_percentage: 18,
      discount_type: 'percentage',
      discount_value: 0,
      line_items: [{ description: '', quantity: 1, unit_rate: 0, tax_applicable: true }],
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout pageTitle="Budget Estimates" pageSubtitle="Create and manage professional budget proposals">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center gap-4 mb-6">
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            >
              <option value="">All Status</option>
              {ESTIMATE_STATUSES.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
            
            <select
              value={approvalStatusFilter}
              onChange={(e) => { setApprovalStatusFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            >
              <option value="">All Approvals</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            <span>New Estimate</span>
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{isEditing ? 'Edit Estimate' : 'Create New Estimate'}</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Header Section */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Estimate Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                    <select
                      name="customer_id"
                      value={formData.customer_id}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Customer</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimate Date *</label>
                    <input
                      type="date"
                      name="estimate_date"
                      value={formData.estimate_date}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until *</label>
                    <input
                      type="date"
                      name="validity_date"
                      value={formData.validity_date}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Type *</label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleFormChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      {PROJECT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Name *</label>
                    <input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleFormChange}
                      placeholder="Website Redesign"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                    <select
                      name="payment_terms"
                      value={formData.payment_terms}
                      onChange={handleFormChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      {PAYMENT_TERMS.map(term => (
                        <option key={term} value={term}>{term}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scope of Work</label>
                  <textarea
                    name="scope_of_work"
                    value={formData.scope_of_work}
                    onChange={handleFormChange}
                    placeholder="Detailed description of what will be delivered..."
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Line Items Section */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Line Items</h3>
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    + Add Item
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Qty</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Rate</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formData.line_items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                              placeholder="Service or product description"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                              min="0.01"
                              step="0.01"
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.unit_rate}
                              onChange={(e) => handleLineItemChange(index, 'unit_rate', e.target.value)}
                              min="0"
                              step="0.01"
                              className="w-24 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </td>
                          <td className="px-4 py-3 font-semibold">
                            ₹{(item.quantity * item.unit_rate).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleRemoveLineItem(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="bg-blue-50 rounded-lg p-6 border-b pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Pricing</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span className="font-semibold">₹{totals.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tax %</label>
                          <input
                            type="number"
                            name="tax_percentage"
                            value={formData.tax_percentage}
                            onChange={handleFormChange}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                          <select
                            name="discount_type"
                            value={formData.discount_type}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value</label>
                        <input
                          type="number"
                          name="discount_value"
                          value={formData.discount_value}
                          onChange={handleFormChange}
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex justify-between pt-3 border-t border-blue-200">
                        <span>Tax ({formData.tax_percentage}%):</span>
                        <span className="font-semibold">₹{totals.tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span className="font-semibold text-green-600">-₹{totals.discount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-3 border-t border-blue-200">
                        <span>Total Amount:</span>
                        <span className="text-blue-600">₹{totals.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Additional Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            name="timeline_start"
                            value={formData.timeline_start}
                            onChange={handleFormChange}
                            placeholder="Start"
                            className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input
                            type="date"
                            name="timeline_end"
                            value={formData.timeline_end}
                            onChange={handleFormChange}
                            placeholder="End"
                            className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Special Notes</label>
                        <textarea
                          name="special_notes"
                          value={formData.special_notes}
                          onChange={handleFormChange}
                          rows="4"
                          placeholder="Any special notes for the client..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  {isEditing ? 'Update Estimate' : 'Create Estimate'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Estimates Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading estimates...</div>
          ) : estimates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No estimates found. Create your first one!</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Project</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Approval</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {estimates.map(est => (
                    <tr
                      key={est.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handlePreview(est.id)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{est.estimateNumber}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{est.customer_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{est.project_name}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{(parseFloat(est.total_amount) || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(est.status)}`}>
                          {est.status.charAt(0).toUpperCase() + est.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          est.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                          est.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {est.approval_status.replace('_', ' ').charAt(0).toUpperCase() + est.approval_status.replace('_', ' ').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(est.id);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                            title="Preview"
                          >
                            <EyeIcon size={18} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(est.id);
                            }}
                            className="text-orange-600 hover:text-orange-700"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowVersions(est.id);
                            }}
                            className="text-purple-600 hover:text-purple-700"
                            title="Versions"
                          >
                            <History size={18} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(est.id);
                            }}
                            className="text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* EstimatePreview Modal */}
        {showPreviewModal && previewEstimate && (
          <EstimatePreview
            estimate={previewEstimate}
            lineItems={previewLineItems}
            customer={previewCustomer}
            company={previewCompany}
            onClose={() => setShowPreviewModal(false)}
            onApprove={handleApprove}
            onReject={handleReject}
            onDownloadPdf={handleDownloadPdf}
            onSendEmail={() => handleSendEmailWithApproval(previewEstimate.id)}
          />
        )}

        {/* Version History Modal */}
        {showVersionHistory && (
          <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/50 p-4 pt-24 backdrop-blur-sm sm:p-6 sm:pt-28">
            <div className="mx-auto flex min-h-[calc(100vh-7rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-xl sm:min-h-[calc(100vh-8rem)]">
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-white">Version History</h3>
                <button
                  onClick={() => setShowVersionHistory(false)}
                  className="text-white text-2xl hover:opacity-80"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-auto p-8">
                {versionHistory.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">No versions found</div>
                ) : (
                  <div className="space-y-4">
                    {versionHistory.map((version, index) => (
                      <div key={version.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Version {version.version_number}
                            </h4>
                            <p className="text-sm text-gray-500">
                              Created on {new Date(version.created_at).toLocaleDateString()} by {version.created_by_name || 'Unknown'}
                            </p>
                          </div>
                          {version.change_description && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {version.change_description}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm mb-2">
                          <div>
                            <p className="text-gray-500">Subtotal</p>
                            <p className="font-semibold">₹{(parseFloat(version.subtotal) || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Tax</p>
                            <p className="font-semibold">₹{(parseFloat(version.tax_amount) || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Discount</p>
                            <p className="font-semibold text-green-600">-₹{(parseFloat(version.discount_amount) || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total</p>
                            <p className="font-bold text-blue-600">₹{(parseFloat(version.total_amount) || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Estimates;
